import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../../supabaseClient';
import { GoogleMap, useLoadScript, PolygonF } from '@react-google-maps/api';
import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { aubergeine, nightMode, retro } from '../../styles/mapStyles';
import {
  BoltIcon,
  XMarkIcon,
  ChartBarIcon,
  MapIcon,
  UserGroupIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  DocumentArrowDownIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  BuildingOfficeIcon,
  ChevronDownIcon,
  CursorArrowRaysIcon,
  SunIcon,
  MoonIcon,
  LightBulbIcon,
  EyeIcon,
  EyeSlashIcon,
  PlayIcon,
  AdjustmentsHorizontalIcon,
  PhotoIcon,
  GlobeAltIcon
} from '@heroicons/react/20/solid';
import PDFReportGenerator from '../../utils/PDFReportGenerator';


// Performance constants
const MAX_RECORDS = 25000;
const MAX_MAP_MARKERS = 15000;

// Default quality thresholds
const DEFAULT_THRESHOLDS = {
  compaction: {
    pass: 92.0,
    warning: 88.0,
    fail: 0.0
  },
  density: {
    pass: 145.0,
    warning: 140.0,
    fail: 0.0
  }
};

// Quality-based marker icons
const qualityMarkerIcons = {
  pass: {
    path: 'M10 20c-5.523 0-10-4.477-10-10S4.477 0 10 0s10 4.477 10 10-4.477 10-10 10z',
    fillColor: '#10B981',
    fillOpacity: 1,
    strokeWeight: 2,
    strokeColor: '#FFFFFF',
    scale: 0.7,
  },
  warning: {
    path: 'M10 20c-5.523 0-10-4.477-10-10S4.477 0 10 0s10 4.477 10 10-4.477 10-10 10z',
    fillColor: '#F59E0B',
    fillOpacity: 1,
    strokeWeight: 2,
    strokeColor: '#FFFFFF',
    scale: 0.7,
  },
  fail: {
    path: 'M10 20c-5.523 0-10-4.477-10-10S4.477 0 10 0s10 4.477 10 10-4.477 10-10 10z',
    fillColor: '#EF4444',
    fillOpacity: 1,
    strokeWeight: 2,
    strokeColor: '#FFFFFF',
    scale: 0.7,
  },
  unknown: {
    path: 'M10 20c-5.523 0-10-4.477-10-10S4.477 0 10 0s10 4.477 10 10-4.477 10-10 10z',
    fillColor: '#6B7280',
    fillOpacity: 1,
    strokeWeight: 2,
    strokeColor: '#FFFFFF',
    scale: 0.7,
  }
};

// Custom cluster renderer
const createClusterRenderer = () => {
  if (!window.google || !window.google.maps) return null;

  return {
    render: ({ count, position }) => {
      const color = count > 1000 ? "#dc2626" : count > 500 ? "#ea580c" : count > 100 ? "#d97706" : "#059669";
      const size = count > 1000 ? 80 : count > 500 ? 70 : count > 100 ? 60 : 50;
      
      return new window.google.maps.Marker({
        position,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          fillColor: color,
          fillOpacity: 0.8,
          strokeWeight: 2,
          strokeColor: "#ffffff",
          scale: size / 2,
        },
        label: {
          text: count > 999 ? `${(count/1000).toFixed(1)}k` : String(count),
          color: "white",
          fontSize: count > 1000 ? "16px" : count > 100 ? "14px" : "12px",
          fontWeight: "bold",
        },
        title: `${count} measurements`,
        zIndex: 1000,
      });
    },
  };
};

// Feature Overview Modal Component
const FeatureOverviewModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const features = [
    {
      icon: <GlobeAltIcon className="h-6 w-6 text-yellow-500" />,
      title: "Interactive Maps",
      description: "Real-time visualization with GPS tracking and quality color coding"
    },
    {
      icon: <AdjustmentsHorizontalIcon className="h-6 w-6 text-yellow-500" />,
      title: "Quality Control",
      description: "Custom thresholds for pass/warning/fail criteria"
    },
    {
      icon: <BuildingOfficeIcon className="h-6 w-6 text-yellow-500" />,
      title: "Project Management",
      description: "Geofenced projects with automatic data assignment"
    },
    {
      icon: <PhotoIcon className="h-6 w-6 text-yellow-500" />,
      title: "Rich Data Points",
      description: "Photos, notes, timestamps, and detailed measurements"
    }
  ];

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-gradient-to-br from-stone-800 to-stone-900 rounded-xl shadow-2xl max-w-2xl w-full border border-stone-700/50">
        {/* Header */}
        <div className="p-6 border-b border-stone-700/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <BoltIcon className="h-8 w-8 text-yellow-500" />
              <div>
                <h2 className="text-xl font-bold text-white">Welcome to the Demo</h2>
                <p className="text-gray-300 text-sm">Explore with real asphalt measurement data</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-stone-700 rounded-lg transition-colors text-gray-400 hover:text-white"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {features.map((feature, index) => (
              <div key={index} className="flex space-x-3 p-3 bg-stone-800/50 rounded-lg border border-stone-700/30">
                <div className="flex-shrink-0 p-2 bg-stone-700/50 rounded-lg">
                  {feature.icon}
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-white mb-1">{feature.title}</h3>
                  <p className="text-gray-400 text-xs">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Quick Tips */}
          <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-semibold text-yellow-500 mb-2 flex items-center">
              <LightBulbIcon className="h-4 w-4 mr-2" />
              Quick Tips
            </h3>
            <ul className="text-xs text-gray-300 space-y-1">
              <li>• Click markers for detailed info and photos</li>
              <li>• Use sidebar projects to filter and navigate</li>
              <li>• Try different map styles in the header</li>
              <li>• Adjust quality thresholds to see real-time changes</li>
            </ul>
          </div>

          {/* Footer Actions */}
          <div className="flex justify-between items-center">
            <div className="text-xs text-gray-500">
              This won't appear again this session
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => window.open('/signup', '_blank')}
                className="bg-transparent border border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-black px-4 py-2 rounded-lg text-xs font-medium transition-colors duration-300"
              >
                Start Trial
              </button>
              <button
                onClick={onClose}
                className="bg-yellow-500 hover:bg-yellow-400 text-black px-4 py-2 rounded-lg text-xs font-medium transition-colors duration-300 flex items-center"
              >
                <PlayIcon className="h-3 w-3 mr-1" />
                Start Demo
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function HQDemo() {
  // Core data states
  const [measurements, setMeasurements] = useState([]);
  const [mapMarkers, setMapMarkers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectsWithGeofences, setProjectsWithGeofences] = useState([]);
  const [operators, setOperators] = useState([]);
  const [allOperators, setAllOperators] = useState([]); // Keep track of all operators
  
  // Statistics states
  const [stats, setStats] = useState({
    totalMeasurements: 0,
    totalProjects: 0,
    totalOperators: 0,
    qualityStats: { pass: 0, warning: 0, fail: 0, unknown: 0, passRate: 0 },
    recentActivity: { today: 0, thisWeek: 0, thisMonth: 0 },
    topProjects: [],
    topOperators: []
  });
  
  // UI states
  const [loading, setLoading] = useState(true);
  const [areFiltersActive, setAreFiltersActive] = useState(false);
  const [selectedProjectFromSidebar, setSelectedProjectFromSidebar] = useState(null);
  
  // Feature Overview Modal state
  const [showFeatureOverview, setShowFeatureOverview] = useState(false);
  const [hasShownModal, setHasShownModal] = useState(false);
  
  // Collapsible sections
  const [sectionsCollapsed, setSectionsCollapsed] = useState({
    stats: false,
    controls: true,
    visibility: true,
    filters: true,
    projects: false
  });
  
  // Quality control states
  const [qualityAttribute, setQualityAttribute] = useState('compaction');
  const [qualityThresholds, setQualityThresholds] = useState(DEFAULT_THRESHOLDS);
  
  // Map states
  const [mapCenter, setMapCenter] = useState({ lat: 40.7128, lng: -74.0060 });
  const [mapZoom, setMapZoom] = useState(16);
  const [mapStyle, setMapStyle] = useState('hybrid');
  const [visibilitySettings, setVisibilitySettings] = useState({
    projects: true,
    measurements: true,
    quality: true,
    clustering: true
  });
  
  // Filter states
  const [filters, setFilters] = useState({
    selectedProject: '',
    selectedOperator: '',
    startDate: '',
    endDate: '',
    qualityFilter: 'all',
    searchTerm: ''
  });

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: 'AIzaSyBBfpoVAWYZaujXG5t93mFIJKqk3cwHynw',
  });

  // Show feature overview when demo loads and map is ready (only once)
  useEffect(() => {
    if (isLoaded && !loading && measurements.length > 0 && !hasShownModal) {
      // Small delay to let the map fully render before showing modal
      const timer = setTimeout(() => {
        setShowFeatureOverview(true);
        setHasShownModal(true);
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [isLoaded, loading, measurements.length, hasShownModal]);

  // Transform demo data to match expected format
  const transformDemoData = useCallback((demoData) => {
    return demoData.map(row => ({
      // Measurement values
      Density: row.density,
      Compaction: row.compaction,
      SurfaceTemp: row.surface_temp,
      LocalTimeOfMeasurement: row.local_time_of_measurement,
      Description1: row.description1,
      notes: row.notes,
      photo_path: row.photo_path,
      
      // Location
      latitude: row.latitude,
      longitude: row.longitude,
      
      // Project info
      project_id: row.project_id,
      project_name: row.project_name,
      project_display_name: row.project_display_name,
      project_geofence: row.project_geofence,
      
      // Operator info
      operator_name: row.operator_name,
      operator_id: row.operator_id,
      
      // Additional fields for compatibility
      id: row.id,
      created_at: row.created_at
    }));
  }, []);

  // Determine quality status
  const getQualityStatus = useCallback((measurement, attribute, thresholds) => {
    const value = measurement[attribute === 'compaction' ? 'Compaction' : 'Density'];
    
    if (value === null || value === undefined || isNaN(value)) {
      return 'unknown';
    }

    const limits = thresholds[attribute];
    
    if (value >= limits.pass) {
      return 'pass';
    } else if (value >= limits.warning) {
      return 'warning';
    } else {
      return 'fail';
    }
  }, []);

  // Calculate quality statistics
  const calculateQualityStats = useCallback((measurementsData, attribute, thresholds) => {
    if (!measurementsData || measurementsData.length === 0) {
      return { pass: 0, warning: 0, fail: 0, unknown: 0, total: 0, passRate: 0 };
    }

    const stats = { pass: 0, warning: 0, fail: 0, unknown: 0, total: measurementsData.length };

    measurementsData.forEach(measurement => {
      const status = getQualityStatus(measurement, attribute, thresholds);
      stats[status]++;
    });

    stats.passRate = stats.total > 0 ? Math.round((stats.pass / stats.total) * 100) : 0;
    return stats;
  }, [getQualityStatus]);

  // Calculate polygon centroid
  const calculatePolygonCentroid = useCallback((polygon) => {
    if (!polygon || polygon.length === 0) return null;
    
    let totalLat = 0, totalLng = 0;
    polygon.forEach(point => {
      totalLat += point.lat;
      totalLng += point.lng;
    });
    
    return {
      lat: totalLat / polygon.length,
      lng: totalLng / polygon.length,
    };
  }, []);

  // Jump to location on map
  const jumpToLocation = useCallback((coord, zoom = 18) => {
    if (!isLoaded || !mapRef.current || !coord) return;
    
    setMapCenter(coord);
    setMapZoom(zoom);
    mapRef.current.panTo(coord);
    mapRef.current.setZoom(zoom);
  }, [isLoaded]);

  // Handle project click from sidebar
  const handleProjectClick = useCallback((project) => {
    setFilters(prev => ({ ...prev, selectedProject: project.name }));
    setSelectedProjectFromSidebar(project);
    
    if (project.polygon && project.polygon.length > 0) {
      const center = calculatePolygonCentroid(project.polygon);
      if (center) {
        jumpToLocation(center, 17);
      }
    }
  }, [calculatePolygonCentroid, jumpToLocation]);

  // Cleanup markers
  const cleanupMarkers = useCallback(() => {
    if (markerClustererRef.current) {
      markerClustererRef.current.clearMarkers();
      markerClustererRef.current = null;
    }
    
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];
    
    if (infoWindowRef.current) {
      infoWindowRef.current.close();
    }
    
    if (hoverTooltipRef.current) {
      hoverTooltipRef.current.close();
    }
  }, []);

  // Refs
  const mapRef = useRef(null);
  const markerClustererRef = useRef(null);
  const markersRef = useRef([]);
  const infoWindowRef = useRef(null);
  const hoverTooltipRef = useRef(null);

  // Create markers with clustering
  const createMarkersWithClustering = useCallback((measurementsData) => {
    if (!mapRef.current || !isLoaded || !measurementsData.length) return;
    
    if (!window.google || !window.google.maps) {
      console.warn('Google Maps API not loaded yet');
      return;
    }

    cleanupMarkers();

    if (!infoWindowRef.current) {
      infoWindowRef.current = new window.google.maps.InfoWindow();
    }

    if (!hoverTooltipRef.current) {
      hoverTooltipRef.current = new window.google.maps.InfoWindow({
        disableAutoPan: true,
        pixelOffset: new window.google.maps.Size(0, -5)
      });
    }

    const markers = measurementsData
      .filter(measurement => measurement.latitude && measurement.longitude)
      .slice(0, MAX_MAP_MARKERS)
      .map(measurement => {
        const qualityStatus = getQualityStatus(measurement, qualityAttribute, qualityThresholds);
        const markerIcon = qualityMarkerIcons[qualityStatus];
        
        const marker = new window.google.maps.Marker({
          position: { lat: measurement.latitude, lng: measurement.longitude },
          icon: markerIcon,
          title: measurement.Description1 || 'Measurement',
          map: null,
        });

        // Hover tooltip
        marker.addListener('mouseover', () => {
          const attributeValue = measurement[qualityAttribute === 'compaction' ? 'Compaction' : 'Density'];
          const attributeLabel = qualityAttribute === 'compaction' ? 'Compaction' : 'Density';
          const statusColor = qualityStatus === 'pass' ? '#10B981' : qualityStatus === 'warning' ? '#F59E0B' : '#EF4444';

          const hoverContent = `
            <div class="p-2 text-xs" style="min-width: 140px;">
              <div class="flex items-center justify-between mb-1">
                <div class="px-1 py-0.5 rounded text-xs font-bold text-white" style="background-color: ${statusColor}; font-size: 10px;">
                  ${qualityStatus.toUpperCase()}
                </div>
              </div>
              <div class="text-gray-600">
                ${attributeLabel}: <span class="font-mono font-medium">${attributeValue?.toFixed(1) || 'N/A'}</span>
              </div>
              <div class="text-gray-600">
                Operator: ${measurement.operator_name || 'Unknown'}
              </div>
            </div>
          `;

          hoverTooltipRef.current.setContent(hoverContent);
          hoverTooltipRef.current.open(mapRef.current, marker);
        });

        marker.addListener('mouseout', () => {
          hoverTooltipRef.current.close();
        });

        // Click for detailed info (condensed version)
        marker.addListener('click', () => {
            const attributeValue = measurement[qualityAttribute === 'compaction' ? 'Compaction' : 'Density'];
            const attributeLabel = qualityAttribute === 'compaction' ? 'Compaction' : 'Density';
            const statusColor = qualityStatus === 'pass' ? '#10B981' : qualityStatus === 'warning' ? '#F59E0B' : '#EF4444';
          
            const buildPhotoUrl = (photoPath) => {
              // For demo, just use the exact working URL you provided
              if (photoPath && photoPath.includes('bills-drive-start')) {
                return 'https://zccgzqkkpmqpawelftbw.supabase.co/storage/v1/object/public/demo-photos/bills-drive-start.jpg';
              }
              
              // Fallback for other photos
              const baseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://zccgzqkkpmqpawelftbw.supabase.co';
              const cleanPath = photoPath ? photoPath.replace(/^\/+/, '').replace(/\/+/g, '/') : '';
              return `${baseUrl}/storage/v1/object/public/${cleanPath}`;
            };
          
            // Get the actual values with proper fallbacks
            const densityValue = measurement.Density !== null && measurement.Density !== undefined ? 
              measurement.Density.toFixed(1) : 'N/A';
            const compactionValue = measurement.Compaction !== null && measurement.Compaction !== undefined ? 
              measurement.Compaction.toFixed(1) : 'N/A';
          
            // Condensed info window with photos and notes
            const photoSection = measurement.photo_path ? `
              <img src="${buildPhotoUrl(measurement.photo_path)}" 
                   style="width: 100%; height: 96px; object-fit: cover; border-radius: 4px; margin-bottom: 8px;" 
                   alt="Measurement photo" 
                   onerror="this.style.display='none'" />
            ` : '';
          
            const notesSection = measurement.notes ? `
              <div style="padding-top: 8px; border-top: 1px solid #e5e7eb; margin-top: 8px;">
                <div style="font-size: 12px; color: #6b7280;">${measurement.notes}</div>
              </div>
            ` : '';
          
            const contentString = `
              <div style="padding: 12px; background: white; border-radius: 8px; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1); min-width: 260px; max-width: 300px; font-family: system-ui, -apple-system, sans-serif;">
                <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px;">
                  <h3 style="font-size: 14px; font-weight: bold; color: #1f2937; margin: 0;">${measurement.Description1 || 'Demo Data Point'}</h3>
                  <div style="padding: 2px 8px; border-radius: 4px; font-size: 12px; font-weight: bold; color: white; background-color: ${statusColor};">
                    ${qualityStatus.toUpperCase()}
                  </div>
                </div>
                
                ${photoSection}
                
                <div style="margin-top: 8px;">
                  <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; font-size: 14px; margin-bottom: 8px;">
                    <div>
                      <span style="color: #6b7280;">Density:</span>
                      <div style="font-family: monospace; font-weight: 500; color: #1f2937;">${densityValue} pcf</div>
                    </div>
                    <div>
                      <span style="color: #6b7280;">Compaction:</span>
                      <div style="font-family: monospace; font-weight: 500; color: #1f2937;">${compactionValue}%</div>
                    </div>
                  </div>
                  
                  <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; font-size: 12px; color: #6b7280; padding-top: 4px;">
                    <div>
                      <span style="font-weight: 500;">Operator:</span>
                      <div style="color: #374151;">${measurement.operator_name || 'Unknown'}</div>
                    </div>
                    <div>
                      <span style="font-weight: 500;">Time:</span>
                      <div style="color: #374151;">${new Date(measurement.LocalTimeOfMeasurement).toLocaleTimeString()}</div>
                    </div>
                  </div>
                  
                  ${notesSection}
                </div>
              </div>
            `;
          
            // Close hover tooltip when showing click popup
            hoverTooltipRef.current.close();
            infoWindowRef.current.setContent(contentString);
            infoWindowRef.current.open(mapRef.current, marker);
          });

        marker.measurement = measurement;
        return marker;
      });

    markersRef.current = markers;

    if (markers.length > 0 && visibilitySettings.clustering) {
      const clusterRenderer = createClusterRenderer();
      if (clusterRenderer) {
        markerClustererRef.current = new MarkerClusterer({
          map: mapRef.current,
          markers: markers,
          renderer: clusterRenderer,
          gridSize: 60,
          maxZoom: 14,
          minimumClusterSize: 2,
          averageCenter: true,
          zoomOnClick: true,
        });
      }
    } else if (markers.length > 0) {
      markers.forEach(marker => marker.setMap(mapRef.current));
    }
  }, [qualityAttribute, qualityThresholds, getQualityStatus, isLoaded, visibilitySettings.clustering]);

 // Fetch demo data
const fetchDemoData = useCallback(async () => {
    setLoading(true);
    try {
      // First, fetch ALL data for projects and geofences (no filters)
      const { data: allDemoData, error: allDataError } = await supabase
        .from('demo_data')
        .select('*')
        .limit(MAX_RECORDS)
        .order('local_time_of_measurement', { ascending: false });
  
      if (allDataError) throw allDataError;
  
      // Transform all data for projects
      const allTransformedData = transformDemoData(allDemoData || []);
  
      // Process project geofences and lists from ALL data
      const projectNames = Array.from(new Set(
        allTransformedData
          .map(m => m.project_display_name)
          .filter(name => name && name !== 'No Project')
      )).sort();
      setProjects(projectNames);
  
      // Get all operators from all data
      const allOperatorNames = Array.from(new Set(
        allTransformedData
          .map(m => m.operator_name)
          .filter(name => name && name !== 'Unknown')
      )).sort();
      setAllOperators(allOperatorNames);
  
      // Filter operators based on selected project
      let filteredOperatorNames = allOperatorNames;
      if (filters.selectedProject) {
        filteredOperatorNames = Array.from(new Set(
          allTransformedData
            .filter(m => m.project_display_name === filters.selectedProject)
            .map(m => m.operator_name)
            .filter(name => name && name !== 'Unknown')
        )).sort();
      }
      setOperators(filteredOperatorNames);
  
      // Process project geofences from ALL data
      const uniqueProjects = [];
      const seenProjects = new Set();
  
      allTransformedData.forEach(item => {
        if (!seenProjects.has(item.project_id) && item.project_geofence) {
          seenProjects.add(item.project_id);
          
          try {
            const geoJson = typeof item.project_geofence === 'string' 
              ? JSON.parse(item.project_geofence) 
              : item.project_geofence;
  
            if (geoJson && geoJson.coordinates && geoJson.coordinates.length > 0) {
              const coords = geoJson.coordinates[0].map(([lng, lat]) => ({ lat, lng }));
              
              const colors = [
                { fill: "#3B82F6", stroke: "#1D4ED8" },
                { fill: "#10B981", stroke: "#047857" },
                { fill: "#F59E0B", stroke: "#D97706" },
                { fill: "#EF4444", stroke: "#DC2626" },
                { fill: "#8B5CF6", stroke: "#7C3AED" },
                { fill: "#06B6D4", stroke: "#0891B2" },
                { fill: "#F97316", stroke: "#EA580C" },
                { fill: "#EC4899", stroke: "#DB2777" },
              ];
              
              const colorIndex = uniqueProjects.length % colors.length;
              const projectColor = colors[colorIndex];
  
              uniqueProjects.push({
                id: item.project_id,
                name: item.project_display_name || item.project_name,
                polygon: coords,
                center: calculatePolygonCentroid(coords),
                options: {
                  fillColor: projectColor.fill,
                  fillOpacity: 0.2,
                  strokeColor: projectColor.stroke,
                  strokeOpacity: 0.8,
                  strokeWeight: 2,
                }
              });
            }
          } catch (parseError) {
            console.warn('Error parsing geofence for project:', item.project_name, parseError);
          }
        }
      });
  
      setProjectsWithGeofences(uniqueProjects);
  
      // Now fetch filtered data for measurements only
      let measurementQuery = supabase
        .from('demo_data')
        .select('*')
        .limit(MAX_RECORDS);
  
      if (filters.selectedProject) {
        measurementQuery = measurementQuery.eq('project_display_name', filters.selectedProject);
      }
      if (filters.selectedOperator) {
        measurementQuery = measurementQuery.eq('operator_name', filters.selectedOperator);
      }
      if (filters.startDate) {
        measurementQuery = measurementQuery.gte('local_time_of_measurement', new Date(filters.startDate).toISOString());
      }
      if (filters.endDate) {
        measurementQuery = measurementQuery.lte('local_time_of_measurement', new Date(filters.endDate).toISOString());
      }
  
      const { data: filteredDemoData, error: filteredError } = await measurementQuery.order('local_time_of_measurement', { ascending: false });
  
      if (filteredError) throw filteredError;
  
      // Transform filtered data for measurements
      const transformedData = transformDemoData(filteredDemoData || []);
      let filteredMeasurements = transformedData;
  
      if (filters.qualityFilter !== 'all') {
        filteredMeasurements = filteredMeasurements.filter(measurement => {
          const qualityStatus = getQualityStatus(measurement, qualityAttribute, qualityThresholds);
          return qualityStatus === filters.qualityFilter;
        });
      }
  
      if (filters.searchTerm) {
        const searchLower = filters.searchTerm.toLowerCase();
        filteredMeasurements = filteredMeasurements.filter(measurement =>
          (measurement.Description1 && measurement.Description1.toLowerCase().includes(searchLower)) ||
          (measurement.operator_name && measurement.operator_name.toLowerCase().includes(searchLower)) ||
          (measurement.project_display_name && measurement.project_display_name.toLowerCase().includes(searchLower))
        );
      }
  
      setMeasurements(filteredMeasurements);
  
      const mapMarkersData = filteredMeasurements
        .filter(m => m.latitude && m.longitude)
        .slice(0, MAX_MAP_MARKERS);
      setMapMarkers(mapMarkersData);
  
      // Calculate stats from filtered measurements
      const qualityStats = calculateQualityStats(filteredMeasurements, qualityAttribute, qualityThresholds);
      
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
      const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
  
      const recentActivity = {
        today: filteredMeasurements.filter(m => new Date(m.LocalTimeOfMeasurement) >= today).length,
        thisWeek: filteredMeasurements.filter(m => new Date(m.LocalTimeOfMeasurement) >= weekAgo).length,
        thisMonth: filteredMeasurements.filter(m => new Date(m.LocalTimeOfMeasurement) >= monthAgo).length
      };
  
      const projectCounts = {};
      filteredMeasurements.forEach(m => {
        if (m.project_display_name) {
          projectCounts[m.project_display_name] = (projectCounts[m.project_display_name] || 0) + 1;
        }
      });
      const topProjects = Object.entries(projectCounts)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 5)
        .map(([name, count]) => ({ name, count }));
  
      const operatorCounts = {};
      filteredMeasurements.forEach(m => {
        if (m.operator_name) {
          operatorCounts[m.operator_name] = (operatorCounts[m.operator_name] || 0) + 1;
        }
      });
      const topOperators = Object.entries(operatorCounts)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 5)
        .map(([name, count]) => ({ name, count }));
  
      setStats({
        totalMeasurements: filteredMeasurements.length,
        totalProjects: projectNames.length,
        totalOperators: allOperatorNames.length, // Use all operators for total count
        qualityStats,
        recentActivity,
        topProjects,
        topOperators
      });
  
      if (mapMarkersData.length > 0 && filters.qualityFilter === 'all' && !filters.selectedProject) {
        const firstMeasurement = mapMarkersData[0];
        setMapCenter({ lat: firstMeasurement.latitude, lng: firstMeasurement.longitude });
        setMapZoom(16);
      }
  
      setAreFiltersActive(
        !!filters.selectedProject ||
        !!filters.selectedOperator ||
        !!filters.startDate ||
        !!filters.endDate ||
        filters.qualityFilter !== 'all' ||
        !!filters.searchTerm
      );
  
    } catch (err) {
      console.error('Error fetching demo data:', err);
    } finally {
      setLoading(false);
    }
  }, [filters, qualityAttribute, qualityThresholds, getQualityStatus, calculateQualityStats, transformDemoData, calculatePolygonCentroid]);
  // Initial data fetch
  useEffect(() => {
    fetchDemoData();
  }, [fetchDemoData]);

  // Update markers when data changes
  useEffect(() => {
    if (mapMarkers.length > 0 && visibilitySettings.measurements) {
      createMarkersWithClustering(mapMarkers);
    } else {
      cleanupMarkers();
    }
  }, [mapMarkers, createMarkersWithClustering, visibilitySettings.measurements]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupMarkers();
    };
  }, [cleanupMarkers]);

  // Export demo data
  const exportData = (format = 'csv') => {
    if (measurements.length === 0) return;

    const headers = [
      'Project', 'Operator', 'Density', 'Compaction', 'Surface Temp',
      'Quality Status', 'Latitude', 'Longitude', 'Local Time', 'Description', 'Notes'
    ];

    const csvData = measurements.map(m => [
      m.project_display_name || 'Demo Project',
      m.operator_name || 'Demo Operator',
      m.Density?.toFixed(1) || '',
      m.Compaction?.toFixed(1) || '',
      m.SurfaceTemp?.toFixed(1) || '',
      getQualityStatus(m, qualityAttribute, qualityThresholds),
      m.latitude?.toFixed(6) || '',
      m.longitude?.toFixed(6) || '',
      new Date(m.LocalTimeOfMeasurement).toLocaleString(),
      m.Description1 || '',
      m.notes || ''
    ]);

    const csvContent = [headers, ...csvData].map(row => 
      row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
    ).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `transtech-demo-export-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Handle map style change
  const handleStyleChange = useCallback((styleName) => {
    setMapStyle(styleName);
  }, []);

  // Get map style options
  const getMapStyleOptions = useCallback(() => {
    const baseOptions = {
        fullscreenControl: false,
        zoomControl: false,
        streetViewControl: false,
        scaleControl: false,
        rotateControl: false,
        gestureHandling: 'greedy'
      };

    switch (mapStyle) {
      case 'aubergeine':
        return { ...baseOptions, styles: aubergeine };
      case 'nightMode':
        return { ...baseOptions, styles: nightMode };
      case 'retro':
        return { ...baseOptions, styles: retro };
      case 'standard':
        return { ...baseOptions, mapTypeId: 'roadmap' };
      case 'hybrid':
      default:
        return { 
          ...baseOptions, 
          mapTypeId: 'hybrid',
          styles: [
            {
              featureType: 'all',
              elementType: 'labels',
              stylers: [{ visibility: 'simplified' }]
            }
          ]
        };
    }
  }, [mapStyle]);

  // Toggle section collapse
  const toggleSection = (section) => {
    setSectionsCollapsed(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Fit map bounds
  const fitMapBounds = useCallback(() => {
    if (!isLoaded || !mapRef.current) return;

    const bounds = new window.google.maps.LatLngBounds();
    let hasPoints = false;

    if (visibilitySettings.projects) {
      projectsWithGeofences.forEach(project => {
        if (project.polygon && project.polygon.length > 0) {
          project.polygon.forEach(point => {
            bounds.extend(new window.google.maps.LatLng(point.lat, point.lng));
            hasPoints = true;
          });
        }
      });
    }

    if (visibilitySettings.measurements) {
      mapMarkers.slice(0, 100).forEach(marker => {
        bounds.extend(new window.google.maps.LatLng(marker.latitude, marker.longitude));
        hasPoints = true;
      });
    }

    if (hasPoints) {
      mapRef.current.fitBounds(bounds);
    }

  }, [isLoaded, visibilitySettings, projectsWithGeofences, mapMarkers]);

  const exitDemo = () => {
    window.location.href = '/'; // Go back to landing page
  };

  const generatePDFReport = async () => {
    const projectInfo = {
      name: selectedProjectFromSidebar?.name || 'Demo Project Report',
      location: 'Buffalo, NY Area',
      contractor: 'Demo Construction Co.',
      manager: 'Demo Project Manager',
    };
  
    const options = {
      companyName: 'TransTech Connect',
      qualityThresholds: qualityThresholds
    };
  
    const result = await PDFReportGenerator.generateAndDownload(
      measurements, 
      projectInfo, 
      options
    );
  
    if (result.success) {
      console.log(`Report generated: ${result.filename}`);
    } else {
      console.error('Report generation failed:', result.error);
    }
  };

  if (loadError) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-50">
        <div className="bg-white rounded-lg p-6 max-w-md">
          <div className="text-red-600 mb-4">Failed to load Google Maps: {loadError.message}</div>
          <button onClick={exitDemo} className="px-4 py-2 bg-gray-300 text-gray-700 rounded">Close Demo</button>
        </div>
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-800">
        <div className="text-white text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400 mx-auto mb-4"></div>
          <div className="text-lg font-semibold">Loading Demo...</div>
          <div className="text-sm text-gray-400 mt-2">Preparing sample measurement data</div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-gray-800 flex flex-col">
      {/* Feature Overview Modal */}
      <FeatureOverviewModal 
        isOpen={showFeatureOverview} 
        onClose={() => setShowFeatureOverview(false)} 
      />

      {/* Demo Banner */}
      <div className="bg-blue-600 text-white px-6 py-2 text-center text-sm">
        <strong>Interactive Demo</strong> - Exploring with sample asphalt density data
        <button 
          onClick={() => window.open('/signup', '_blank')}
          className="ml-4 bg-white text-blue-600 px-3 py-1 rounded text-xs font-medium hover:bg-gray-100"
        >
          Start Free Trial
        </button>
      </div>

      {/* Header */}
      <div className="bg-gray-900 border-b border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <BoltIcon className="h-8 w-8 text-yellow-400" />
              <h1 className="text-2xl font-bold text-white">Measurement HQ</h1>
              <span className="bg-yellow-600 text-black text-xs px-2 py-1 rounded font-medium">
                LIVE DEMO
              </span>
            </div>
          </div>

          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-1 bg-gray-800 rounded-lg p-1">
              <button
                onClick={() => handleStyleChange('hybrid')}
                className={`p-2 rounded-md transition-colors ${
                  mapStyle === 'hybrid' ? 'bg-yellow-600 text-white' : 'text-gray-300 hover:text-white hover:bg-gray-600'
                }`}
                title="Hybrid View"
              >
                <MapIcon className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleStyleChange('standard')}
                className={`p-2 rounded-md transition-colors ${
                  mapStyle === 'standard' ? 'bg-yellow-600 text-white' : 'text-gray-300 hover:text-white hover:bg-gray-600'
                }`}
                title="Standard View"
              >
                <SunIcon className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleStyleChange('nightMode')}
                className={`p-2 rounded-md transition-colors ${
                  mapStyle === 'nightMode' ? 'bg-yellow-600 text-white' : 'text-gray-300 hover:text-white hover:bg-gray-600'
                }`}
                title="Night Mode"
              >
                <MoonIcon className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleStyleChange('aubergeine')}
                className={`p-2 rounded-md transition-colors ${
                  mapStyle === 'aubergeine' ? 'bg-yellow-600 text-white' : 'text-gray-300 hover:text-white hover:bg-gray-600'
                }`}
                title="Aubergeine Mode"
              >
                <BoltIcon className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleStyleChange('retro')}
                className={`p-2 rounded-md transition-colors ${
                  mapStyle === 'retro' ? 'bg-yellow-600 text-white' : 'text-gray-300 hover:text-white hover:bg-gray-600'
                }`}
                title="Retro Mode"
              >
                <LightBulbIcon className="h-4 w-4" />
              </button>
            </div>
            
            <button
              onClick={fitMapBounds}
              className="px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
            >
              <MapIcon className="h-4 w-4 mr-1 inline" />
              Fit Bounds
            </button>
            
            <button
              onClick={() => exportData('csv')}
              className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
              disabled={measurements.length === 0}
            >
              <DocumentArrowDownIcon className="h-4 w-4 mr-1 inline" />
              Export Demo Data
            </button>

            <button
              onClick={() => setShowFeatureOverview(true)}
              className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
              title="Show Feature Overview"
            >
              <LightBulbIcon className="h-4 w-4 mr-1 inline" />
              Features
            </button>
            
            <button
              onClick={exitDemo}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg"
              title="Exit Demo"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Left Sidebar - Controls & Stats */}
        <div className="w-[20vw] bg-gray-900 border-r border-gray-700 flex flex-col overflow-hidden">
        <div className="flex-1 overflow-y-auto">
          {/* Demo Stats */}
          <div className="border-b border-gray-700">
            <button
              onClick={() => toggleSection('stats')}
              className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-800 transition-colors"
            >
              <h3 className="text-md font-semibold text-white">Demo Stats</h3>
              <div className={`transform transition-transform ${sectionsCollapsed.stats ? '' : 'rotate-180'}`}>
                <ChevronDownIcon className="w-5 h-5 text-gray-400" />
              </div>
            </button>
            
            {!sectionsCollapsed.stats && (
              <div className="px-4 pb-4">
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-gray-800 rounded-lg p-3">
                    <div className="text-2xl font-bold text-blue-400">{stats.totalMeasurements.toLocaleString()}</div>
                    <div className="text-xs text-gray-300">Sample Measurements</div>
                  </div>
                  <div className="bg-gray-800 rounded-lg p-3">
                    <div className="text-2xl font-bold text-green-400">{stats.totalProjects}</div>
                    <div className="text-xs text-gray-300">Demo Projects</div>
                  </div>
                  <div className="bg-gray-800 rounded-lg p-3">
                    <div className="text-2xl font-bold text-yellow-400">{stats.totalOperators}</div>
                    <div className="text-xs text-gray-300">Demo Operators</div>
                  </div>
                  <div className="bg-gray-800 rounded-lg p-3">
                    <div className="text-2xl font-bold text-purple-400">{stats.qualityStats.passRate}%</div>
                    <div className="text-xs text-gray-300">Pass Rate</div>
                  </div>
                </div>

                {/* Quality Breakdown */}
                <div className="bg-gray-800 rounded-lg p-3 mb-3">
                  <div className="text-sm font-medium text-white mb-2">Quality Control Demo</div>
                  <div className="grid grid-cols-4 gap-2 text-xs">
                    <div className="text-center">
                      <div className="font-bold text-green-400">{stats.qualityStats.pass}</div>
                      <div className="text-gray-300">Pass</div>
                    </div>
                    <div className="text-center">
                      <div className="font-bold text-yellow-400">{stats.qualityStats.warning}</div>
                      <div className="text-gray-300">Warning</div>
                    </div>
                    <div className="text-center">
                      <div className="font-bold text-red-400">{stats.qualityStats.fail}</div>
                      <div className="text-gray-300">Fail</div>
                    </div>
                    <div className="text-center">
                      <div className="font-bold text-gray-400">{stats.qualityStats.unknown}</div>
                      <div className="text-gray-300">Unknown</div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Demo Projects */}
          <div className="border-b border-gray-700">
            <button
              onClick={() => toggleSection('projects')}
              className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-800 transition-colors"
            >
              <h3 className="text-md font-semibold text-white">Demo Projects</h3>
              <div className={`transform transition-transform ${sectionsCollapsed.projects ? '' : 'rotate-180'}`}>
                <ChevronDownIcon className="w-5 h-5 text-gray-400" />
              </div>
            </button>
            
            {!sectionsCollapsed.projects && (
              <div className="px-4 pb-4">
                <div className="text-xs text-gray-400 mb-3">
                  Click projects to jump to location and filter data
                </div>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {projectsWithGeofences.map((project, idx) => (
                    <div 
                      key={project.id}
                      onClick={() => handleProjectClick(project)}
                      className={`cursor-pointer p-2 rounded-lg transition-all hover:bg-gray-600 ${
                        filters.selectedProject === project.name ? 'bg-yellow-600 text-white' : 'bg-gray-800'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <div 
                            className="w-3 h-3 rounded-full border border-white"
                            style={{ backgroundColor: project.options.fillColor }}
                          ></div>
                          <span className="text-sm font-medium text-white truncate">
                            {project.name}
                          </span>
                        </div>
                        <CursorArrowRaysIcon className="h-4 w-4 text-gray-400" />
                      </div>
                      {filters.selectedProject === project.name && (
                        <div className="text-xs text-yellow-200 mt-1">
                          📍 Active Filter
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {projectsWithGeofences.length === 0 && (
                    <div className="text-center py-4 text-gray-400">
                      <BuildingOfficeIcon className="h-8 w-8 mx-auto mb-2" />
                      <div className="text-sm">Loading demo projects...</div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Quality Controls */}
          <div className="border-b border-gray-700">
            <button
              onClick={() => toggleSection('controls')}
              className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-800 transition-colors"
            >
              <h3 className="text-md font-semibold text-white">Quality Controls</h3>
              <div className={`transform transition-transform ${sectionsCollapsed.controls ? '' : 'rotate-180'}`}>
                <ChevronDownIcon className="w-5 h-5 text-gray-400" />
              </div>
            </button>
            
            {!sectionsCollapsed.controls && (
              <div className="px-4 pb-4">
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1">Quality Attribute</label>
                    <select
                      value={qualityAttribute}
                      onChange={(e) => setQualityAttribute(e.target.value)}
                      className="w-full bg-gray-800 border border-gray-600 rounded text-white text-sm"
                    >
                      <option value="compaction">Compaction (%)</option>
                      <option value="density">Density (pcf)</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1">Pass Threshold</label>
                    <input
                      type="number"
                      step="0.1"
                      value={qualityThresholds[qualityAttribute].pass}
                      onChange={(e) => setQualityThresholds(prev => ({
                        ...prev,
                        [qualityAttribute]: {
                          ...prev[qualityAttribute],
                          pass: parseFloat(e.target.value) || 0
                        }
                      }))}
                      className="w-full bg-gray-800 border border-gray-600 rounded text-white text-sm"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1">Warning Threshold</label>
                    <input
                      type="number"
                      step="0.1"
                      value={qualityThresholds[qualityAttribute].warning}
                      onChange={(e) => setQualityThresholds(prev => ({
                        ...prev,
                        [qualityAttribute]: {
                          ...prev[qualityAttribute],
                          warning: parseFloat(e.target.value) || 0
                        }
                      }))}
                      className="w-full bg-gray-800 border border-gray-600 rounded text-white text-sm"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Demo Filters */}
          <div className="border-b border-gray-700">
            <button
              onClick={() => toggleSection('filters')}
              className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-800 transition-colors"
            >
              <h3 className="text-md font-semibold text-white">Data Filters</h3>
              <div className={`transform transition-transform ${sectionsCollapsed.filters ? '' : 'rotate-180'}`}>
                <ChevronDownIcon className="w-5 h-5 text-gray-400" />
              </div>
            </button>
            {!sectionsCollapsed.filters && (
              <div className="px-4 pb-4">
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1">Search</label>
                    <div className="relative">
                      <MagnifyingGlassIcon className="absolute left-2 top-2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        value={filters.searchTerm}
                        onChange={(e) => setFilters(prev => ({ ...prev, searchTerm: e.target.value }))}
                        placeholder="Search demo data..."
                        className="w-full bg-gray-800 border border-gray-600 rounded text-white text-sm pl-8"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1">Operator</label>
                    <select
                      value={filters.selectedOperator}
                      onChange={(e) => setFilters(prev => ({ ...prev, selectedOperator: e.target.value }))}
                      className="w-full bg-gray-800 border border-gray-600 rounded text-white text-sm"
                    >
                      <option value="">All Operators</option>
                      {operators.map(operator => (
                        <option key={operator} value={operator}>{operator}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1">Quality Filter</label>
                    <select
                      value={filters.qualityFilter}
                      onChange={(e) => setFilters(prev => ({ ...prev, qualityFilter: e.target.value }))}
                      className="w-full bg-gray-800 border border-gray-600 rounded text-white text-sm"
                    >
                      <option value="all">All Quality Levels</option>
                      <option value="pass">Pass Only</option>
                      <option value="warning">Warning Only</option>
                      <option value="fail">Fail Only</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-300 mb-1">Start Date</label>
                      <input
                        type="date"
                        value={filters.startDate}
                        onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                        className="w-full bg-gray-800 border border-gray-600 rounded text-white text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-300 mb-1">End Date</label>
                      <input
                        type="date"
                        value={filters.endDate}
                        onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                        className="w-full bg-gray-800 border border-gray-600 rounded text-white text-sm"
                      />
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setFilters({
                        selectedProject: '',
                        selectedOperator: '',
                        startDate: '',
                        endDate: '',
                        qualityFilter: 'all',
                        searchTerm: ''
                      });
                      setSelectedProjectFromSidebar(null);
                    }}
                    className="w-full px-3 py-1 bg-gray-600 text-white text-xs rounded hover:bg-gray-500"
                  >
                    Clear All Filters
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Map Visibility */}
          <div className="border-b border-gray-700">
            <button
              onClick={() => toggleSection('visibility')}
              className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-800 transition-colors"
            >
              <h3 className="text-md font-semibold text-white">Map Controls</h3>
              <div className={`transform transition-transform ${sectionsCollapsed.visibility ? '' : 'rotate-180'}`}>
                <ChevronDownIcon className="w-5 h-5 text-gray-400" />
              </div>
            </button>
            
            {!sectionsCollapsed.visibility && (
              <div className="px-4 pb-4">
                <div className="space-y-2">
                  {Object.entries(visibilitySettings).map(([key, value]) => (
                    <label key={key} className="flex items-center space-x-2 text-sm">
                      <input
                        type="checkbox"
                        checked={value}
                        onChange={(e) => setVisibilitySettings(prev => ({ ...prev, [key]: e.target.checked }))}
                        className="rounded border-gray-600 bg-gray-800 text-yellow-600"
                      />
                      <span className="text-gray-300 capitalize">{key.replace(/([A-Z])/g, ' $1')}</span>
                      {value ? <EyeIcon className="h-4 w-4 text-green-400" /> : <EyeSlashIcon className="h-4 w-4 text-gray-500" />}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          </div>
        </div>

        {/* Main Map Area */}
        <div className="flex-1 relative">
          <GoogleMap
            center={mapCenter}
            zoom={mapZoom}
            mapContainerStyle={{ width: '100%', height: '100%' }}
            options={getMapStyleOptions()}
            onLoad={(map) => {
              mapRef.current = map;
            }}
            onUnmount={() => {
              mapRef.current = null;
            }}
          >
            {/* Project Geofences */}
            {visibilitySettings.projects && projectsWithGeofences.map(project => (
              <React.Fragment key={`project_${project.id}`}>
                <PolygonF
                  path={project.polygon}
                  options={{
                    ...project.options,
                    fillOpacity: filters.selectedProject === project.name ? 0.4 : 0.2,
                    strokeWeight: filters.selectedProject === project.name ? 3 : 2,
                    zIndex: filters.selectedProject === project.name ? 1000 : 1
                  }}
                  onClick={() => handleProjectClick(project)}
                />
              </React.Fragment>
            ))}
          </GoogleMap>

          {/* Map Info Overlay */}
          <div className="absolute top-4 right-4 bg-gray-900 bg-opacity-80 text-white p-4 rounded-lg text-sm space-y-2">
            <div className="font-bold mb-2">Demo Status</div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-500 rounded"></div>
              <span>Pass ({stats.qualityStats.pass})</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-yellow-500 rounded"></div>
              <span>Warning ({stats.qualityStats.warning})</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-red-500 rounded"></div>
              <span>Fail ({stats.qualityStats.fail})</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-gray-500 rounded"></div>
              <span>Unknown ({stats.qualityStats.unknown})</span>
            </div>
            <div className="border-t border-gray-600 pt-2 mt-2">
              <div className="text-xs text-gray-300">
                {mapMarkers.length.toLocaleString()} demo markers
              </div>
              <div className="text-xs text-blue-300">
                {projectsWithGeofences.length} demo projects
              </div>
              <div className="text-xs text-purple-300">
                Map Style: {mapStyle.charAt(0).toUpperCase() + mapStyle.slice(1)}
              </div>
            </div>
            {/* Clear Filters Button */}
            {areFiltersActive && (
              <div className="mt-4">
                <button
                  onClick={() => {
                    setFilters({
                      selectedProject: '',
                      selectedOperator: '',
                      startDate: '',
                      endDate: '',
                      qualityFilter: 'all',
                      searchTerm: ''
                    });
                    setSelectedProjectFromSidebar(null);
                  }}
                  className="w-full px-3 py-2 bg-gray-600 text-white text-sm rounded hover:bg-gray-500 flex items-center justify-center"
                  disabled={loading}
                  title="Reset all filters to default"
                >
                  <FunnelIcon className="h-4 w-4 mr-2" />
                  Clear All Filters
                  {loading && <div className="ml-2 w-4 h-4 border-2 border-t-transparent border-white rounded-full animate-spin"></div>}
                </button>
              </div>
            )}
          </div>

          {/* Selected Project Info */}
          {filters.selectedProject && (
            <div className="absolute bottom-4 left-4 bg-yellow-600 text-white p-3 rounded-lg text-sm max-w-sm">
              <div className="flex items-center space-x-2">
                <BuildingOfficeIcon className="h-5 w-5" />
                <div>
                  <div className="font-semibold">Demo Project Filter</div>
                  <div className="text-xs text-yellow-200">{filters.selectedProject}</div>
                </div>
              </div>
            </div>
          )}

          {/* Performance Warning */}
          {mapMarkers.length >= MAX_MAP_MARKERS && (
            <div className="absolute bottom-4 right-4 bg-orange-600 text-white p-3 rounded-lg text-sm max-w-sm">
              <div className="flex items-center space-x-2">
                <ExclamationTriangleIcon className="h-5 w-5" />
                <div>
                  <div className="font-semibold">Demo Performance Mode</div>
                  <div className="text-xs">Showing {MAX_MAP_MARKERS.toLocaleString()} of {stats.totalMeasurements.toLocaleString()} demo measurements</div>
                </div>
              </div>
            </div>
          )}

          {/* Call to Action Overlay */}
          <div className="absolute bottom-4 right-4 bg-gradient-to-r from-yellow-600 to-yellow-500 text-black p-4 rounded-lg shadow-lg max-w-sm">
            <h4 className="font-bold mb-2">Like what you see?</h4>
            <p className="text-sm mb-3">Connect your real PQI equipment and start managing your asphalt data today.</p>
            <div className="flex gap-2">
              <button
                onClick={() => window.open('/signup', '_blank')}
                className="bg-black text-yellow-400 px-3 py-2 rounded text-sm font-medium hover:bg-gray-800 flex-1"
              >
                Start Free Trial
              </button>
              <button
                onClick={() => window.open('https://transtechsys.com/products/salesRequest', '_blank')}
                className="bg-yellow-700 text-white px-3 py-2 rounded text-sm hover:bg-yellow-800"
              >
                Schedule Call
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Status Bar */}
      <div className="bg-gray-900 border-t border-gray-700 px-6 py-2">
        <div className="flex items-center justify-between text-sm text-gray-300">
          <div className="flex items-center space-x-4">
            <span>🔍 Demo Mode Active</span>
            {loading && (
              <span className="flex items-center space-x-1">
                <div className="w-3 h-3 bg-yellow-400 rounded-full animate-pulse"></div>
                <span>Loading demo data...</span>
              </span>
            )}
            {!loading && (
              <span className="flex items-center space-x-1">
                <CheckCircleIcon className="w-4 h-4 text-green-400" />
                <span>Demo systems operational</span>
              </span>
            )}
          </div>
          
          <div className="flex items-center space-x-4">
            <span>Sample Data: {stats.totalMeasurements.toLocaleString()} measurements</span>
            <span>Quality: {stats.qualityStats.passRate}% Pass</span>
            <span>Projects: {projectsWithGeofences.length}</span>
            <button
              onClick={exitDemo}
              className="px-4 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Exit Demo
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}